import React, {
  FocusEventHandler,
  SyntheticEvent,
  useEffect,
  useState,
  CSSProperties,
} from "react";

import {
  Tooltip,
  Typography,
  Checkbox,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import {
  colors,
  fontSize,
  fontWeight,
  lineHeight,
  padding,
} from "../../../theme/defaultStyle";
import { styled } from "@mui/material/styles";

export type OptionRenderType = {
  label: string;
  value: string;
};

export type StewartAutocompleteEventType = {
  target: { name: string; value: OptionRenderType[] };
};

type StewartAutocompleteProps<T> = {
  placeholder?: string;
  name: keyof T;
  options: any[];
  optionsTextField?: string;
  optionsValueField?: string;
  disabled?: boolean;
  clearSelectionOnDisable?: boolean;
  onChange?: (
    e: StewartAutocompleteEventType,
    value: OptionRenderType[]
  ) => void;
  onBlur?: FocusEventHandler<HTMLDivElement>;
  controlStyle?: CSSProperties;
  readOnly?: boolean;
  hasUntouchedValidation?: boolean;
  required?: boolean;
  freeSolo?: boolean;
  defaultSelectedOption?: OptionRenderType | OptionRenderType[];
  getOptionDisabled?: (option: OptionRenderType) => boolean;
  onOpen?: (event?: SyntheticEvent) => void;
  onClose?: (event?: SyntheticEvent) => void;
  textFieldRef?: React.RefObject<HTMLInputElement>;
  hasErrorIn?: boolean;
  inputClassName?: string;
  errorMessage?: string;
};

const CustomTextField = styled(TextField)(() => ({
  "& .MuiInputBase-input.Mui-disabled": {
    backgroundColor: colors.white01,
  },
  "& input::placeholder": {
    color: `${colors.white01} !important`,
  },
}));

const StyledAutocomplete = styled(Autocomplete)(() => ({
  "& .MuiInputBase-root": { height: "40px", padding: `${padding.zero} ${padding.large1} !important` },
  "& .MuiOutlinedInput-root": {
    flexWrap: "nowrap !important",
    "&.Mui-focused fieldset": {
      border: `2px solid ${colors.blue01} !important`,
    },
    "&.Mui-disabled": {
      backgroundColor: colors.white01,
    },
    "& .MuiOutlinedInput-input": {
      padding: `${padding.zero} ${padding.xsmall2} !important`,
      height: "40px !important",
    },
  },
  "& .MuiInputLabel-outlined": {
    "&.MuiInputLabel-shrink": {
      top: "2px !important",
    },
  },
  ".MuiSvgIcon-root": {
    fill: `${colors.blue01} !important`,
  },
}));

const StyledTypography = styled(Typography)(() => ({
  fontSize: `${fontSize.large} !important`,
  fontWeight: `${fontWeight.normal1} !important`,
  lineHeight: `${lineHeight.small1} !important`,
}));

const StewartAutocompleteWithCheckboxes = <T,>({
  placeholder = "",
  name,
  optionsTextField = "label",
  optionsValueField = "value",
  options,
  onChange = () => {},
  onBlur = () => null,
  controlStyle = {},
  readOnly = false,
  disabled = false,
  clearSelectionOnDisable = false,
  hasUntouchedValidation = false,
  required = false,
  freeSolo = false,
  getOptionDisabled,
  onOpen,
  onClose,
  defaultSelectedOption,
  hasErrorIn,
  inputClassName = "",
  errorMessage = "",
}: StewartAutocompleteProps<T>) => {
  const [fieldValue, setFieldValue] = useState<OptionRenderType[]>(() => {
    // Initialize with default value if provided
    if (defaultSelectedOption) {
      return Array.isArray(defaultSelectedOption) ? defaultSelectedOption : [defaultSelectedOption];
    }
    return [];
  });

  const optionsToRender: OptionRenderType[] = options
    .map((option) => ({
      label: option[optionsTextField],
      value: option[optionsValueField],
    }))
    .filter((option) => option.label);

  useEffect(() => {
    if (options.length === 1 && fieldValue.length === 0) {
      const singleOption = {
        label: options[0][optionsTextField],
        value: options[0][optionsValueField],
      };
      setFieldValue([singleOption]);
      onChange(
        { target: { name: name.toString(), value: [singleOption] } },
        [singleOption]
      );
    }
  }, [options]);

  useEffect(() => {
    if (clearSelectionOnDisable && disabled && fieldValue.length > 0) {
      setFieldValue([]);
      onChange({ target: { name: name.toString(), value: [] } }, []);
    }
  }, [disabled, clearSelectionOnDisable, onChange, name, fieldValue.length]);

  const hasError =
    (fieldValue.length === 0 && hasUntouchedValidation && !defaultSelectedOption) || Boolean(errorMessage);

  const handleOnChange = (
    event: SyntheticEvent,
    value: OptionRenderType[],
  ) => {
    setFieldValue(value);
    onChange(
      { target: { name: name.toString(), value } },
      value
    );
  };

  return (
    <StyledAutocomplete
      id={name.toString()}
      options={optionsToRender}
      value={fieldValue}
      onChange={(event, value, reason, details) => handleOnChange(event, value as OptionRenderType[])}
      onBlur={onBlur}
      onOpen={onOpen}
      onClose={onClose}
      getOptionDisabled={(option) => Boolean(getOptionDisabled?.(option as OptionRenderType))}
      isOptionEqualToValue={(option, value) =>
        (option as OptionRenderType).value === (value as OptionRenderType).value
      }
      getOptionLabel={(option) => (option as OptionRenderType).label}
      renderTags={(value) => (
        <Tooltip
          title={(value as OptionRenderType[])
            .map((elem) => elem.label)
            .join(", ")}
          placement="right"
          arrow
        >
          <StyledTypography noWrap>{

            (value as OptionRenderType[])
              .map((elem) => elem.label)
              .join(", ")
          }</StyledTypography>
        </Tooltip>
      )}
      renderOption={(props, option, { selected }) => (
        <li {...props}>
          <Checkbox
            sx={{ "& .MuiSvgIcon-root": { color: colors.blue01 } }}
            checked={selected}
          />
          {(option as OptionRenderType).label}
        </li>
      )}
      renderInput={(params) => (
        <CustomTextField
          {...params}
          label={placeholder !== "Search" ? placeholder : ""}
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            style: { padding: padding.zero },
          }}
          InputLabelProps={{
            shrink: Boolean(fieldValue.length) || Boolean(params.inputProps?.value),
          }}
          variant="outlined"
          required={required}
          error={hasError || hasErrorIn}
        />
      )}
      multiple
      disableCloseOnSelect
      disableClearable
      readOnly={readOnly}
      disabled={disabled}
      freeSolo={freeSolo}
      style={{ display: "flex", flexDirection: "column", width: "100%", ...controlStyle }}
      className={inputClassName}
    />
  );
};

export default StewartAutocompleteWithCheckboxes;
